import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { Button } from './Button';

interface EmptyStateProps {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Optional second, lower-emphasis action shown alongside the primary one. */
  secondaryAction?: { label: string; onClick: () => void };
  illustration?: ReactNode;
  /** Semantic heading rank for `title`. Defaults to `3` (`<h3>`), matching the
   * previous hardcoded markup — set explicitly when this EmptyState isn't at
   * the level a plain `<h3>` would imply in the surrounding page. */
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
}

// Empty state — icon + one sentence + primary action (P10 §2.3).
export function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
  secondaryAction,
  illustration,
  headingLevel = 3,
}: EmptyStateProps): JSX.Element {
  const Heading = `h${headingLevel}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  return (
    <div className="rounded-hairline border border-border bg-card flex flex-col items-center gap-4 px-6 py-12 text-center">
      <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20" aria-hidden>
        {illustration}
      </div>
      <div>
        <Heading className="text-lg font-semibold text-foreground">{title}</Heading>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">{description}</p>
      </div>
      {(actionLabel || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {actionLabel && (
            <Button onClick={onAction} icon={<ArrowLongRightIcon className="h-4 w-4" />} iconPlacement="right">
              {actionLabel}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
